import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Navigate, catchAll, useTypedParams } from '@sorare/routing';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { BottomSheet } from '@sorare/core/src/atoms/layout/BottomSheet';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { ProfilePageHeader } from '@sorare/core/src/components/navigation/ProfilePageHeader';
import {
  ANY_SPORT_MY_CARDS,
  ANY_SPORT_MY_CARDS_BOXES,
  ANY_SPORT_MY_CARDS_BOXES_TYPE,
  ANY_SPORT_MY_CARDS_CARDS,
  ANY_SPORT_MY_CARDS_CARDS_TYPE,
  ANY_SPORT_MY_CARDS_COLLECTIONS,
  ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE,
  ANY_SPORT_MY_CARDS_ESSENCE,
  ANY_SPORT_MY_CARDS_ESSENCE_TYPE,
  ANY_SPORT_MY_CLUB_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { useQueryLoaderResult } from '@sorare/core/src/hooks/useQueryLoaderResult';
import { fantasy, galleryTabs } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { shardsIconUrl } from '@sorare/core/src/lib/rewards';

import {
  MyCardsLayoutQuery,
  MyCardsLayoutQueryVariables,
} from './__generated__/index.graphql';
import { MyCardsLayoutOutletContext } from './types';

const BottomSheetWrapper = styled.div`
  position: relative;
  width: 100vw;
`;

const InnerBottomSheetWrapper = styled.div`
  position: absolute;
  /** FIXME: remove this hack once above-bottom-bar-portal doesn't have default padding-bottom */
  bottom: calc(-1 * var(--double-unit));
  left: 0;
  right: 0;
  pointer-events: none;
  & > * {
    pointer-events: auto;
  }
`;

const RarityWrapper = styled(SBHorizontal)`
  padding-bottom: var(--double-unit);
`;

const Box = styled(ButtonBase)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--half-unit);
  padding: var(--unit);
  width: 64px;
  height: 64px;
  border-radius: var(--intermediate-unit);
  border: 1px solid var(--c-nd-150);
  & > * {
    opacity: 0.5;
  }
  &.active {
    border-color: var(--c-neutral-1000);
    & > * {
      opacity: 1;
    }
  }
`;

const BOTTOM_SHEET_RARITY_SWITCHER_HEIGHT = 90;

type Props = {
  includeCommonCards?: boolean;
};

// use to keep last user selected state on My Cards revisit
let userSelectedState: boolean;

const MyCardsLayoutComponent = ({
  currentUser,
  includeCommonCards,
  type,
}: Props & {
  currentUser: MyCardsLayoutQuery['currentUser'];
  type: Nullable<Rarity | 'rivals'>;
}) => {
  const {
    flags: { enableMyClubSplit = false },
  } = useFeatureFlags();
  const { generateSportContextPath, matchAnySportPath, sport } =
    useSportContext();
  const isTabletAndAbove = useIsTabletAndAbove();
  const location = useLocation();
  const [visibleSheetHeight, setVisibleSheetHeight] = useState(0);
  if (!enableMyClubSplit) {
    return (
      <Navigate to={generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG)} replace />
    );
  }

  const tabs = (
    <>
      <Tab
        to={generateSportContextPath(ANY_SPORT_MY_CARDS)}
        matches={[
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_CARDS)),
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_COLLECTIONS)),
        ]}
      >
        <FormattedMessage {...galleryTabs.cards} />
      </Tab>
      <Tab
        to={generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES)}
        matches={[catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES))]}
      >
        <FormattedMessage {...fantasy.boxes} />
      </Tab>
      <Tab
        to={generateSportContextPath(ANY_SPORT_MY_CARDS_ESSENCE)}
        matches={[
          catchAll(generateSportContextPath(ANY_SPORT_MY_CARDS_ESSENCE)),
        ]}
      >
        <FormattedMessage {...fantasy.essenceShort} />
      </Tab>
    </>
  );

  if (isTabletAndAbove) {
    return (
      <>
        <ProfilePageHeader />
        <ContentWithStickyHeader
          header={
            <TabBar fullWidth variant="flat">
              {tabs}
            </TabBar>
          }
        >
          <Outlet />
        </ContentWithStickyHeader>
      </>
    );
  }

  const {
    unopenedRewardBoxLimited,
    unopenedRewardBoxRare,
    unopenedRewardBoxSuperRare,
    unopenedRewardBoxUnique,
    unopenedRewardBoxRivals,
    anyUserSportProfile,
    cardShardsChests,
  } = currentUser || {};

  const cardsCount = anyUserSportProfile?.cardsCount;
  const cardsCountByRarity = {
    [Rarity.limited]: cardsCount?.limited || 0,
    [Rarity.rare]: cardsCount?.rare || 0,
    [Rarity.super_rare]: cardsCount?.superRare || 0,
    [Rarity.unique]: cardsCount?.unique || 0,
  };
  const cardsCountByRarityWithCommon = {
    [Rarity.common]: cardsCount?.common || 0,
    ...cardsCountByRarity,
  };
  const finalCardsCountByRarity = includeCommonCards
    ? cardsCountByRarityWithCommon
    : cardsCountByRarity;
  const blockchainRarities = [
    Rarity.limited,
    Rarity.rare,
    Rarity.super_rare,
    Rarity.unique,
  ] as const;
  const boxTypes = [
    ...blockchainRarities,
    ...(sport === Sport.FOOTBALL ? ['rivals'] : []),
  ] as const;

  const unopenedProbabilisticBundlesByRarity: Record<
    (typeof boxTypes)[number],
    number
  > = {
    limited: unopenedRewardBoxLimited?.totalCount ?? 0,
    rare: unopenedRewardBoxRare?.totalCount ?? 0,
    super_rare: unopenedRewardBoxSuperRare?.totalCount ?? 0,
    unique: unopenedRewardBoxUnique?.totalCount ?? 0,
    rivals: unopenedRewardBoxRivals?.totalCount ?? 0,
  };

  const hasMoreThan1Rarity =
    Object.values(finalCardsCountByRarity).filter(Boolean).length > 1;

  const matchCollectionsPage = matchAnySportPath(
    catchAll(ANY_SPORT_MY_CARDS_COLLECTIONS),
    location.pathname
  );
  return (
    <>
      <ProfilePageHeader />
      <Outlet
        context={
          {
            bottomSheetHeight: visibleSheetHeight,
            defaultRarity: Object.entries(finalCardsCountByRarity).find(
              ([, value]) => !!value
            )?.[0] as Rarity,
            cardsCountByRarity: finalCardsCountByRarity,
          } satisfies MyCardsLayoutOutletContext
        }
      />
      <Portal id="above-bottom-bar-portal">
        <BottomSheetWrapper>
          <InnerBottomSheetWrapper>
            <BottomSheet
              snapPoints={[BOTTOM_SHEET_RARITY_SWITCHER_HEIGHT]}
              withGrabber
              openInitially={
                typeof userSelectedState !== 'undefined'
                  ? userSelectedState
                  : hasMoreThan1Rarity
              }
              onChange={(value: boolean) => {
                userSelectedState = value;
              }}
              setVisibleHeight={setVisibleSheetHeight}
            >
              <Vertical center gap={3}>
                <TabBar variant="primary" compact>
                  {tabs}
                </TabBar>
                <RarityWrapper>
                  {(matchAnySportPath(
                    catchAll(ANY_SPORT_MY_CARDS_CARDS),
                    location.pathname
                  ) ||
                    matchCollectionsPage) &&
                    Object.entries(finalCardsCountByRarity)
                      .filter(
                        ([key, value]) =>
                          (!!value ||
                            // show limited tab when on Collections by default because we don't have common collections yet
                            (matchCollectionsPage && key === Rarity.limited)) &&
                          !(matchCollectionsPage && key === Rarity.common)
                      )
                      .map(([key, value]) => (
                        <Box
                          key={key}
                          to={generateSportContextPath(
                            matchCollectionsPage
                              ? ANY_SPORT_MY_CARDS_COLLECTIONS_TYPE
                              : ANY_SPORT_MY_CARDS_CARDS_TYPE,
                            {
                              params: {
                                type: key,
                              },
                            }
                          )}
                          className={classNames({
                            active: type === key,
                          })}
                        >
                          <NewScarcityIcon
                            rarity={key as keyof typeof finalCardsCountByRarity}
                            size={5}
                          />
                          <LabelM bold>{value}</LabelM>
                        </Box>
                      ))}
                  {matchAnySportPath(
                    catchAll(ANY_SPORT_MY_CARDS_BOXES),
                    location.pathname
                  ) &&
                    boxTypes.map(key => (
                      <Box
                        key={key}
                        to={generateSportContextPath(
                          ANY_SPORT_MY_CARDS_BOXES_TYPE,
                          {
                            params: {
                              type: key,
                            },
                          }
                        )}
                        className={classNames({
                          active: type === key,
                        })}
                      >
                        <img
                          src={`${FRONTEND_ASSET_HOST}/football/probabilistic_bundles/${
                            key === 'rivals'
                              ? 'rivals-daily-claim-3'
                              : `${key}-standard`
                          }/picture.png`}
                          alt=""
                          width={32}
                          height={32}
                        />
                        <LabelM bold>
                          {unopenedProbabilisticBundlesByRarity[key]}
                        </LabelM>
                      </Box>
                    ))}
                  {matchAnySportPath(
                    catchAll(ANY_SPORT_MY_CARDS_ESSENCE),
                    location.pathname
                  ) &&
                    blockchainRarities.map(key => {
                      const shardsChest = cardShardsChests?.find(
                        chest => chest.rarity === key
                      );
                      return (
                        <Box
                          key={key}
                          to={generateSportContextPath(
                            ANY_SPORT_MY_CARDS_ESSENCE_TYPE,
                            {
                              params: {
                                type: key,
                              },
                            }
                          )}
                          className={classNames({
                            active: type === key,
                          })}
                        >
                          <img
                            src={shardsIconUrl(key)}
                            alt=""
                            width={20}
                            height={32}
                          />

                          <LabelM bold>
                            {shardsChest?.cardShardsCount || 0}
                          </LabelM>
                        </Box>
                      );
                    })}
                </RarityWrapper>
              </Vertical>
            </BottomSheet>
          </InnerBottomSheetWrapper>
        </BottomSheetWrapper>
      </Portal>
    </>
  );
};

const MY_CARDS_LAYOUT_QUERY = gql`
  query MyCardsLayoutQuery($sport: Sport!) {
    currentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        cardsCount {
          common
          limited
          rare
          superRare
          unique
        }
      }
      unopenedRewardBoxLimited: unopenedProbabilisticBundles(
        sport: $sport
        first: 0
        rarity: limited
        design: PRO
      ) {
        totalCount
      }
      unopenedRewardBoxRare: unopenedProbabilisticBundles(
        sport: $sport
        first: 0
        rarity: rare
        design: PRO
      ) {
        totalCount
      }
      unopenedRewardBoxSuperRare: unopenedProbabilisticBundles(
        sport: $sport
        first: 0
        rarity: super_rare
        design: PRO
      ) {
        totalCount
      }
      unopenedRewardBoxUnique: unopenedProbabilisticBundles(
        sport: $sport
        first: 0
        rarity: unique
        design: PRO
      ) {
        totalCount
      }
      unopenedRewardBoxRivals: unopenedProbabilisticBundles(
        sport: $sport
        first: 0
        design: RIVALS
      ) {
        totalCount
      }
      cardShardsChests(sport: $sport) {
        id
        rarity
        cardShardsCount
        newThresholdUnlocked
      }
    }
  }
` as TypedDocumentNode<MyCardsLayoutQuery, MyCardsLayoutQueryVariables>;

const isNullableOrRarityStr = (
  type: Nullable<string>
): type is Nullable<Rarity> => !type || type in Rarity;

export const MyCardsLayout = <
  Params extends {
    type?: string;
  },
>(
  props: Props
) => {
  const { type } = useTypedParams<Params>();
  const { generateSportContextPath } = useSportContext();
  const data = useQueryLoaderResult<MyCardsLayoutQuery>();
  if (!isNullableOrRarityStr(type) && type !== 'rivals') {
    return (
      <Navigate replace to={generateSportContextPath(ANY_SPORT_MY_CARDS)} />
    );
  }
  return (
    <MyCardsLayoutComponent
      currentUser={data.currentUser}
      type={type}
      {...props}
    />
  );
};

export const loader = queryLoader(MY_CARDS_LAYOUT_QUERY, ({ sport }) => ({
  sport,
}));
